import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Instagram,
  Music2,
  Plus,
  Search,
  User,
  X,
  Youtube,
} from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/marketing/components/ui/dialog";
import { useDebounce } from "@/marketing/hooks/useDebounce";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";
import type { Influencer } from "@/marketing/models/influencer";
import { canTransition, getStageLabel } from "@/marketing/lib/campaignPipeline";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { campaignPipelineAPIClient } from "@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient";
import { applyQuiet } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/applyQuiet";
import { useCampaignQuietStore } from "@/marketing/stores/useCampaignQuietStore";
import { CAMPAIGN_PIPELINE_KEYS } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys";
import { influencerAPIClient } from "@/marketing/infrastructure/influencer/influencerAPIClient";
import { formatNumber } from "@/marketing/lib/formatters";
import { ProductChoiceGrid } from "../creators/ProductChoiceGrid";
import { buildCampaignProductSelectionState } from "../creators/campaignProductSelection";

interface AddInfluencerModalProps {
  campaignId: string;
  campaign: InfluencerCampaign;
  pipelineDefinition: CampaignPipelineDefinition;
  open: boolean;
  onClose: () => void;
  /** Stage to add into. When provided, the stage selector is locked. */
  defaultStage?: string | undefined;
  lockStage?: boolean | undefined;
  /** Influencer IDs already on the campaign (used for "On campaign" disabled state). */
  existingIds: string[];
}

const PAGE_SIZE = 12;

function PlatformIcon({ platform }: { platform: string }) {
  if (platform === "tiktok") return <Music2 className="tw-h-3 tw-w-3" />;
  if (platform === "instagram") return <Instagram className="tw-h-3 tw-w-3" />;
  if (platform === "youtube") return <Youtube className="tw-h-3 tw-w-3" />;
  return <User className="tw-h-3 tw-w-3" />;
}

export function AddInfluencerModal({
  campaignId,
  campaign,
  pipelineDefinition,
  open,
  onClose,
  defaultStage,
  lockStage = false,
  existingIds,
}: AddInfluencerModalProps) {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const addedBy = currentUser?.email ?? "unknown";

  const { initialStage } = pipelineDefinition;
  const firstStage = defaultStage ?? initialStage;
  const [query, setQuery] = useState("");
  const [stage, setStage] = useState<string>(firstStage);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);

  const productSelectionState = useMemo(
    () => buildCampaignProductSelectionState(campaign, selectedSkus),
    [campaign, selectedSkus],
  );
  const isChoiceRewardCampaign = productSelectionState.isRequired;
  const productSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedId(null);
      setSelectedSkus([]);
      setStage(defaultStage ?? initialStage);
    }
  }, [open, defaultStage, initialStage]);

  useEffect(() => {
    if (isChoiceRewardCampaign && selectedId) {
      productSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedId, isChoiceRewardCampaign]);

  const debouncedQuery = useDebounce(query, 300);

  const searchQuery = useQuery({
    queryKey: ["addInfluencerSearch", debouncedQuery],
    queryFn: () =>
      influencerAPIClient.searchInfluencers(
        {
          search: debouncedQuery,
          notSelected: false,
          minFollowers: null,
          engRateMin: null,
          countryCodes: [],
          languages: [],
          hasEmail: null,
          platform: "",
        },
        { pageIndex: 0, pageSize: PAGE_SIZE },
      ),
    staleTime: 30 * 1000,
    enabled: open,
  });

  const onIds = useMemo(() => new Set(existingIds), [existingIds]);

  const results = useMemo(() => {
    const data = searchQuery.data?.data ?? [];
    return [...data].sort((a, b) => {
      const aOn = onIds.has(a.id) ? 1 : 0;
      const bOn = onIds.has(b.id) ? 1 : 0;
      if (aOn !== bOn) return aOn - bOn;
      return b.followersCount - a.followersCount;
    });
  }, [searchQuery.data, onIds]);

  const selected = useMemo(
    () => results.find((r) => r.id === selectedId) ?? null,
    [results, selectedId],
  );

  const addMutation = useMutation({
    mutationFn: async ({
      influencer,
      targetStage,
    }: {
      influencer: Influencer;
      targetStage: string;
    }) => {
      const addResponse =
        await campaignPipelineAPIClient.addCampaignInfluencers(campaignId, {
          influencers: [
            {
              influencerId: influencer.id,
              platform: influencer.platform,
              handle: influencer.handle,
              ...(isChoiceRewardCampaign && { selectedSkus }),
            },
          ],
          addedBy,
        });

      // facade always seeds new rows at the pipeline's initial stage — transition forward if a different stage was picked.
      if (targetStage !== initialStage && addResponse.added > 0) {
        await campaignPipelineAPIClient.transitionCampaignInfluencerStage(
          campaignId,
          influencer.id,
          applyQuiet(
            {
              toStage: targetStage,
              transitionBy: addedBy,
              fastTrack: canTransition(
                pipelineDefinition,
                initialStage,
                targetStage,
              ).requiresFastTrack,
            },
            useCampaignQuietStore.getState().isQuiet(campaignId),
          ),
        );
      }
      return addResponse;
    },
    onSuccess: (response, vars) => {
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_PIPELINE_KEYS.lists(),
      });
      if (response.added > 0) {
        toast.success(
          `Added ${vars.influencer.handle} to ${getStageLabel(pipelineDefinition, vars.targetStage)}`,
        );
        onClose();
      } else {
        toast.info(`${vars.influencer.handle} is already on the campaign`);
      }
    },
    onError: (err: unknown) => {
      const message =
        typeof err === "object" && err !== null && "message" in err
          ? String((err as { message?: unknown }).message)
          : "Failed to add influencer";
      toast.error(message);
    },
  });

  const productSelectionComplete =
    !isChoiceRewardCampaign || productSelectionState.isComplete;
  const canAdd =
    selected !== null &&
    !onIds.has(selected.id) &&
    !addMutation.isPending &&
    productSelectionComplete;

  const handleAdd = () => {
    if (!selected) return;
    addMutation.mutate({ influencer: selected, targetStage: stage });
  };

  const handleInfluencerSelect = (influencerId: string) => {
    if (influencerId === selectedId) return;
    setSelectedId(influencerId);
    setSelectedSkus([]);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="tw-flex tw-max-h-[85vh] tw-max-w-2xl tw-flex-col tw-gap-0 tw-overflow-hidden tw-p-0">
        <div className="tw-shrink-0 tw-border-b tw-border-border tw-px-5 tw-py-4 tw-pr-12">
          <DialogTitle className="tw-text-base tw-font-semibold">
            Add influencer to campaign
          </DialogTitle>
          <p className="tw-text-xs tw-text-muted-foreground">
            Search the influencer pool and place them in a pipeline stage.
          </p>
        </div>

        <div className="tw-shrink-0 tw-border-b tw-border-border tw-px-5 tw-py-3">
          <div className="tw-relative">
            <Search className="tw-absolute tw-left-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or @handle"
              className="tw-h-10 tw-pl-9 tw-pr-9"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-rounded tw-p-1 tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-foreground"
              >
                <X className="tw-h-3.5 tw-w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
          <div className="tw-px-2 tw-py-2">
          {searchQuery.isLoading && (
            <div className="tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground">
              Searching influencers…
            </div>
          )}
          {searchQuery.isError && (
            <div className="tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-red-600">
              Search failed. Try a different query.
            </div>
          )}
          {searchQuery.isSuccess && results.length === 0 && (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-px-4 tw-py-12 tw-text-center tw-text-sm tw-text-muted-foreground">
              <Search className="tw-h-5 tw-w-5 tw-opacity-40" />
              <div>
                No influencers match{" "}
                {debouncedQuery ? `"${debouncedQuery}"` : "your search"}
              </div>
              <div className="tw-text-xs">Try a different name or handle.</div>
            </div>
          )}
          {results.map((p) => {
            const isOn = onIds.has(p.id);
            const isSel = p.id === selectedId;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => !isOn && handleInfluencerSelect(p.id)}
                disabled={isOn}
                className={cn(
                  "tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-md tw-px-3 tw-py-2 tw-text-left tw-transition-colors",
                  !isOn && !isSel && "hover:tw-bg-muted",
                  isSel && "tw-bg-blue-50 tw-ring-1 tw-ring-blue-200",
                  isOn && "tw-cursor-not-allowed tw-opacity-60",
                )}
              >
                <div className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-[11px] tw-font-semibold tw-text-foreground/70">
                  {(p.displayName || p.handle)
                    .split(" ")
                    .map((s) => s[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase() || "??"}
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-truncate tw-text-[13px] tw-font-medium">
                    {p.displayName || p.handle}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-truncate tw-text-[11px] tw-text-muted-foreground">
                    <PlatformIcon platform={p.platform} />@{p.handle}
                    <span>·</span>
                    {formatNumber(p.followersCount)} followers
                    {p.countrySignals.length > 0 && (
                      <>
                        <span>·</span>
                        {p.countrySignals[0]}
                      </>
                    )}
                  </div>
                </div>
                {p.engagementRate !== undefined && (
                  <div className="tw-flex tw-flex-col tw-items-end tw-text-[11px]">
                    <span className="tw-font-semibold tw-tabular-nums">
                      {(p.engagementRate * 100).toFixed(1)}%
                    </span>
                    <span className="tw-text-muted-foreground">eng.</span>
                  </div>
                )}
                <span className="tw-ml-2 tw-flex-shrink-0">
                  {isOn ? (
                    <span className="tw-rounded-full tw-bg-muted tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-muted-foreground">
                      On campaign
                    </span>
                  ) : isSel ? (
                    <span className="tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-600 tw-text-white">
                      <Check className="tw-h-3 tw-w-3" />
                    </span>
                  ) : (
                    <span className="tw-inline-flex tw-h-5 tw-w-5 tw-rounded-full tw-border tw-border-border" />
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {isChoiceRewardCampaign && selected && (
          <div ref={productSectionRef} className="tw-border-t tw-border-border tw-px-5 tw-py-3">
            {productSelectionState.hasSufficientOptions ? (
              <>
                <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                    Pick products for @{selected.handle}
                  </span>
                  <span className="tw-text-[11px] tw-text-muted-foreground">
                    {selectedSkus.length}/{productSelectionState.totalChoiceCount} selected
                  </span>
                </div>
                <ProductChoiceGrid
                  campaign={campaign}
                  selectedSkus={selectedSkus}
                  onChange={setSelectedSkus}
                />
              </>
            ) : (
              <div className="tw-rounded-md tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-text-amber-800">
                This campaign does not have enough available product options.
                Add product allocations or update its group choices before
                enrolling creators.
              </div>
            )}
          </div>
        )}
        </div>

        <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-border tw-bg-muted/40 tw-px-5 tw-py-3">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
            <span className="tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
              Add to stage
            </span>
            {lockStage ? (
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-blue-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-blue-700">
                <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-blue-600" />
                {getStageLabel(pipelineDefinition, stage)}
                <span className="tw-ml-1 tw-text-[10px] tw-font-normal tw-text-muted-foreground">
                  Locked
                </span>
              </span>
            ) : (
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="tw-h-8 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-2 tw-text-xs"
              >
                {pipelineDefinition.stages.map(({ stage: s, labels }) => (
                  <option
                    key={s}
                    value={s}
                    disabled={
                      s !== initialStage &&
                      !canTransition(pipelineDefinition, initialStage, s)
                        .allowed
                    }
                  >
                    {labels.admin}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canAdd}
              onClick={handleAdd}
              className="tw-gap-1.5"
            >
              <Plus className="tw-h-3.5 tw-w-3.5" />
              {selected
                ? `Add ${selected.displayName || selected.handle}`
                : "Add influencer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
